import { Phone, MapPin, Clock, MessageCircle, Navigation2 } from "lucide-react";
import PageWrapper from "@/components/layout/PageWrapper";
import Breadcrumb from "@/components/Breadcrumb";
import EnquiryForm from "@/components/EnquiryForm";
import { useLanguage } from "@/i18n/LanguageContext";
import { CONTACT } from "@/data/content";

const Contact = () => {
  const { t } = useLanguage();
  const waLink = `https://wa.me/${CONTACT.whatsappNumber}?text=${encodeURIComponent(
    "Hello ADHD School, I'd like to know more about admissions.",
  )}`;

  return (
    <PageWrapper
      title="Contact — ADHD School & Bhadrak Physiotherapy"
      description="Visit Aachary Dev Development Holistic School and Bhadrak Physiotherapy & Rehab Centre — address, phone and Google Maps."
    >
      <section className="adhd-container pt-10 lg:pt-14">
        <Breadcrumb pageKey="nav.contact" />
        <p className="adhd-eyebrow mt-6">
          <MapPin size={13} /> Visit / Call / WhatsApp
        </p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-[color:var(--adhd-text)]">
          {t("contact.title")}
        </h1>
        <p className="mt-3 text-[color:var(--adhd-muted)] max-w-2xl">{t("contact.subtitle")}</p>
      </section>

      <section className="adhd-container mt-10 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7">
          <EnquiryForm />
        </div>

        <aside className="lg:col-span-5 flex flex-col gap-5">
          <div className="adhd-card p-6">
            <div className="flex items-start gap-3">
              <span className="grid place-items-center h-10 w-10 rounded-xl shrink-0" style={{ background: "rgba(58,124,165,0.10)" }}>
                <Phone size={18} style={{ color: "var(--adhd-primary)" }} />
              </span>
              <div>
                <p className="text-xs uppercase tracking-wider text-[color:var(--adhd-muted)]">
                  {t("contact.phoneLabel")}
                </p>
                <a
                  href={`tel:${CONTACT.phoneTel}`}
                  data-testid="contact-phone"
                  className="font-serif text-xl text-[color:var(--adhd-text)]"
                >
                  {CONTACT.phone}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3 mt-5">
              <span className="grid place-items-center h-10 w-10 rounded-xl shrink-0" style={{ background: "rgba(90,147,103,0.18)" }}>
                <MapPin size={18} style={{ color: "var(--adhd-success)" }} />
              </span>
              <div>
                <p className="text-xs uppercase tracking-wider text-[color:var(--adhd-muted)]">
                  {t("contact.addressLabel")}
                </p>
                <p className="text-sm leading-relaxed text-[color:var(--adhd-text)] mt-1">
                  {CONTACT.address}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 mt-5">
              <span className="grid place-items-center h-10 w-10 rounded-xl shrink-0" style={{ background: "rgba(230,194,41,0.20)" }}>
                <Clock size={18} style={{ color: "#b58c00" }} />
              </span>
              <div>
                <p className="text-xs uppercase tracking-wider text-[color:var(--adhd-muted)]">
                  {t("contact.hoursLabel")}
                </p>
                <p className="text-sm text-[color:var(--adhd-text)] mt-1">{t("contact.hours")}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-7">
              <a
                href={`tel:${CONTACT.phoneTel}`}
                data-testid="contact-call-btn"
                className="adhd-btn-primary !py-3 !text-xs"
              >
                <Phone size={13} /> {t("cta.call")}
              </a>
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-xs font-semibold text-white"
                style={{ backgroundColor: "#25D366" }}
              >
                <MessageCircle size={13} /> {t("cta.whatsapp")}
              </a>
              <a
                href={CONTACT.mapShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-directions-btn"
                className="adhd-btn-secondary !py-3 !text-xs sm:col-span-2"
              >
                <Navigation2 size={13} /> {t("cta.directions")}
              </a>
            </div>
          </div>

          <div className="adhd-card overflow-hidden">
            <iframe
              title="ADHD School Location"
              src={CONTACT.mapEmbedSrc}
              width="100%"
              height="300"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="map-embed"
              style={{ border: 0 }}
              allowFullScreen
            />
          </div>
        </aside>
      </section>

      {/* JSON-LD Local Business */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationalOrganization",
            name: CONTACT.schoolName,
            telephone: CONTACT.phone,
            address: {
              "@type": "PostalAddress",
              streetAddress: "Bonth Chhak, Netaji Nagar Road, Near LIC Office, Bagurai",
              addressLocality: "Bhadrak",
              addressRegion: "Odisha",
              postalCode: "756100",
              addressCountry: "IN",
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: CONTACT.rating,
              reviewCount: CONTACT.reviewCount,
            },
          }),
        }}
      />
    </PageWrapper>
  );
};

export default Contact;
